import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';

import { useUserRoster } from '@/context/UserRosterContext';
import { useLeagueSettings } from '@/context/LeagueSettingsContext';
import { LoadingSpinner, PageLoader } from '@/components/LoadingSpinner';
import { SleeperImport } from '@/components/SleeperImport';
import { getProcessedSleeperData } from '@/utils/sleeperIntegration';
import { calculateOverallPlayerScore, filterPlayersWithSummary } from '@/utils/playerValue';
import { adjustProjectionsForScoring } from '@/utils/sleeperRosterIntegration';
import type { Player, PositionalScarcity } from '@/types/player';
import type { LeagueSettings as PlayerValueLeagueSettings } from '@/utils/playerValue';
import type { LeagueSettings as TradeLeagueSettings } from '@/types/trade';
import { getPlayerImageWithOverrides } from '@/utils/playerImageFix';

// Positional scarcity coefficients
const POSITIONAL_SCARCITY: Record<string, PositionalScarcity> = {
  TE: { position: 'TE', tierBreakpoints: [200, 150, 120, 100, 80], scarcityMultiplier: 1.8, replacementThreshold: 15 },
  QB: { position: 'QB', tierBreakpoints: [300, 250, 220, 200, 180], scarcityMultiplier: 1.2, replacementThreshold: 12 },
  RB: { position: 'RB', tierBreakpoints: [250, 200, 160, 130, 100], scarcityMultiplier: 1.4, replacementThreshold: 24 },
  WR: { position: 'WR', tierBreakpoints: [220, 180, 150, 120, 100], scarcityMultiplier: 1.0, replacementThreshold: 36 },
  K: { position: 'K', tierBreakpoints: [120, 100, 90, 80, 70], scarcityMultiplier: 0.8, replacementThreshold: 12 }
};

// Convert trade league settings to player value league settings
function convertTradeSettingsToPlayerValue(tradeSettings: TradeLeagueSettings): PlayerValueLeagueSettings {
  return {
    leagueSize: tradeSettings.teamCount,
    startingSlots: {
      QB: tradeSettings.roster.QB,
      RB: tradeSettings.roster.RB,
      WR: tradeSettings.roster.WR,
      TE: tradeSettings.roster.TE,
      K: tradeSettings.roster.K || 1,
      DEF: tradeSettings.roster.DEF || 1,
      FLEX: tradeSettings.roster.FLEX,
      SUPERFLEX: tradeSettings.roster.SUPERFLEX || 0
    }
  };
}

const MyRoster = () => {
  const { isSignedIn } = useUser();
  const { roster, addToRoster, removeFromRoster, isPlayerInRoster, replaceRoster, isLoading: rosterLoading } = useUserRoster();
  const { leagueData, updateFromSleeper, hasImportedSettings } = useLeagueSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [playersPool, setPlayersPool] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Default league settings
  const defaultLeagueSettings: PlayerValueLeagueSettings = useMemo(() => ({
    leagueSize: 12,
    startingSlots: { QB: 1, RB: 2, WR: 2, TE: 1, K: 1, DEF: 1 }
  }), []);

  // Use league settings from context
  const activeLeagueSettings: PlayerValueLeagueSettings = useMemo(() => {
    return convertTradeSettingsToPlayerValue(leagueData.settings);
  }, [leagueData.settings]);

  // Recalculate player values when league settings change
  const recalculatePlayerValues = useCallback((players: Player[], leagueSettings: PlayerValueLeagueSettings, tradeLeagueSettings?: TradeLeagueSettings) => {
    let adjustedPlayers = players;
    
    // First, adjust projections if we have trade league settings (with scoring rules)
    if (tradeLeagueSettings) {
      adjustedPlayers = adjustProjectionsForScoring(players, tradeLeagueSettings);
    }
    
    // Calculate replacement levels with potentially adjusted projections
    const tempReplacementLevels: Record<string, number> = {};
    Object.entries(POSITIONAL_SCARCITY).forEach(([pos, { replacementThreshold }]) => {
      const posPlayers = adjustedPlayers.filter(p => p.position === pos)
                                .sort((a, b) => (b.projection || 0) - (a.projection || 0));
      tempReplacementLevels[pos] = posPlayers[replacementThreshold - 1]?.projection || 0;
    });
    
    // Recalculate scores with new league settings and adjusted projections
    return adjustedPlayers.map(player => {
      const { overallScore } = calculateOverallPlayerScore(player, leagueSettings, adjustedPlayers, tempReplacementLevels[player.position] || 0);
      return { ...player, value: overallScore };
    });
  }, []);

  useEffect(() => {
    // Prevent multiple simultaneous fetches
    if (hasInitialized || isLoadingPlayers) return;
    
    async function fetchPlayers() {
      console.log('MyRoster: Starting player fetch');
      setIsLoadingPlayers(true);
      setHasInitialized(true);
      try {
        const { statsMap, projections: projectionsMap, playerStatsMap, teamUsageMap, teamStatsMap, leagueAverages } =
          await getProcessedSleeperData(2024);
        
        const sleeperRes = await fetch('https://api.sleeper.app/v1/players/nfl');
        if (!sleeperRes.ok) throw new Error('Failed to fetch Sleeper data');
        const sleeperData = await sleeperRes.json();
        const playersArray = Object.values(sleeperData);
        
        const espnRes = await fetch('https://sports.core.api.espn.com/v3/sports/football/nfl/athletes?limit=18000');
        if (!espnRes.ok) throw new Error('Failed to fetch ESPN data');
        const espnData = await espnRes.json();
        const espnAthletes = espnData.items || [];
        
        const nameToEspnId: Record<string, string> = {};
        espnAthletes.forEach((athlete: any) => {
          if (athlete.fullName && athlete.id) {
            nameToEspnId[athlete.fullName.toLowerCase()] = athlete.id.toString();
          }
        });
        
        const validPlayers = filterPlayersWithSummary(playersArray as any[], 'MyRoster', true);
        
        const mapped = validPlayers.map((p: any) => {
          const id = p.player_id;
          const position = p.position as 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';
          const team = p.team || '';
          const stats = statsMap.get(id) || ({} as any);
          const projection = projectionsMap[id] || 0;
          const games = stats.games_played ?? stats.gp ?? 1;
          const previousPPG = (stats.fpts_ppr ?? stats.fpts_half_ppr ?? stats.fpts_std ?? 0) / games;
          const playerStats = playerStatsMap.get(id) || { targets: 0, carries: 0, redZoneOpportunities: 0, airYards: 0, schedule: [] };
          const playerTeamStats = teamUsageMap.get(team) || { targets: 0, carries: 0, redZoneOpportunities: 0, airYards: 0, schedule: [] };
          const teamStats = teamStatsMap.get(team) || { playsPerGame: 65, pointsPerGame: 23, redZoneScoreRate: 0.58, offensiveLineRank: 16 };
          const age = p.age || 27;
          const playerName = p.full_name || 'Unknown Player';
          const espnId = playerName ? nameToEspnId[playerName.toLowerCase()] : undefined;
          const imageUrl = getPlayerImageWithOverrides(
            playerName,
            espnId,
            'https://a.espncdn.com/i/headshots/nophoto.png'
          );

          return {
            id,
            name: playerName,
            position,
            team,
            rank: 0,
            change: 0,
            imageUrl,
            projection,
            age,
            lastYearPoints: previousPPG * games,
            teamStats,
            leagueAverages,
            playerStats,
            playerTeamStats,
            injuryHistory: [],
            value: 0,
            isActive: p.active !== false,
            lastUpdated: new Date().toISOString()
          } as Player;
        });
        
        // Calculate scores with current league settings
        const scored = recalculatePlayerValues(mapped, activeLeagueSettings, hasImportedSettings ? leagueData.settings : undefined);
        
        setPlayersPool(scored);
        console.log('MyRoster: Successfully loaded', scored.length, 'players');
      } catch (e) {
        console.error('Error fetching players:', e);
        // Reset flags on error so user can retry
        setHasInitialized(false);
      } finally {
        setIsLoadingPlayers(false);
      }
    }
    
    fetchPlayers();
  }, [activeLeagueSettings, recalculatePlayerValues, leagueData.settings, hasImportedSettings]);

  // Recalculate player values when league settings change
  useEffect(() => {
    if (playersPool.length > 0) {
      console.log('Recalculating player values due to league settings change...');
      const recalculatedPlayers = recalculatePlayerValues(playersPool, activeLeagueSettings, hasImportedSettings ? leagueData.settings : undefined);
      setPlayersPool(recalculatedPlayers);
    }
  }, [activeLeagueSettings, leagueData.settings, hasImportedSettings, recalculatePlayerValues]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    const lower = query.toLowerCase();
    const results = playersPool
      .filter((p) => p.name && p.name.toLowerCase().includes(lower) && !isPlayerInRoster(String(p.id)))
      .slice(0, 15);
    setSearchResults(results);
    setIsSearching(false);
  }, [playersPool, isPlayerInRoster]);

  const handleAddPlayer = useCallback((player: Player) => {
    addToRoster(player);
    setSearchQuery('');
    setSearchResults([]);
  }, [addToRoster]);

  const handleSleeperImport = useCallback((players: Player[], leagueSettings?: TradeLeagueSettings, leagueName?: string) => {
    if (players.length === 0) {
      console.log('No players found to import');
      return;
    }
    
    // Replace the entire roster with imported players
    replaceRoster(players);
    
    // Update league settings in context if provided
    if (leagueSettings && leagueName) {
      updateFromSleeper(leagueSettings, leagueName);
      console.log(`Successfully imported league settings from ${leagueName}!`);
    }
    
    console.log(`Successfully imported ${players.length} players from Sleeper!`);
  }, [replaceRoster, updateFromSleeper]);

  const getRosterByPosition = () => {
    const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
    return positions.map(pos => ({
      position: pos,
      players: roster.filter(p => p.position === pos)
    }));
  };

  const totalRosterValue = roster.reduce((sum, player) => sum + (player.value || 0), 0);

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-black py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-fiesta-yellow mb-8">
            My Roster
          </h1>
          <p className="text-gray-300 text-lg mb-8">
            Please sign in to manage your fantasy football roster and get personalized trade suggestions.
          </p>
        </div>
      </div>
    );
  }

  if (isLoadingPlayers) {
    return (
      <PageLoader 
        pageName="My Roster"
        subtitle="Build and manage your fantasy football roster to get better trade suggestions"
        loadingText="Loading player data..."
      />
    );
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-4"
      >
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-fiesta-yellow">
            My Roster
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Build and manage your fantasy football roster to get better trade suggestions.
          </p>
          {roster.length > 0 && (
            <div className="mt-4 inline-block bg-gray-800 border border-gray-600 rounded-lg px-4 py-2">
              <span className="text-gray-300 text-sm md:text-base">Total Roster Value: </span>
              <span className="font-bold text-fiesta-yellow text-sm md:text-base">{totalRosterValue.toFixed(2)}</span>
            </div>
          )}
          {hasImportedSettings && (
            <div className="mt-2 inline-block bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-lg px-4 py-2">
              <span className="text-purple-300 text-sm">League Settings from: </span>
                             <span className="font-medium text-purple-100 text-sm">{leagueData.leagueName}</span>
              <span className="text-purple-400 text-xs ml-2">✨ Player values updated</span>
            </div>
          )}
        </div>

        {/* Import Options */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex flex-wrap justify-center gap-4">
            <SleeperImport 
              onImportSuccess={handleSleeperImport}
              availablePlayers={playersPool}
            />
          </div>
        </div>

        {/* League Settings Display */}
        {hasImportedSettings && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-gray-800/70 to-gray-700/70 border border-gray-600 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-fiesta-blue flex items-center gap-2">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  My League Settings
                </h3>
              </div>
              
              {/* League Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">League Format</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Teams:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.teamCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Superflex:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.superflex ? '✅ Yes' : '❌ No'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Scoring System</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Reception:</span>
                      <span className="font-medium text-fiesta-yellow">
                        {leagueData.settings.scoring.pointsPerReception > 0 ? 
                          `${leagueData.settings.scoring.pointsPerReception} PPR` : 
                          'Standard'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pass TD:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.scoring.pointsPerPassTD} pts</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Rush/Rec TD:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.scoring.pointsPerRushTD} pts</span>
                    </div>
                    {leagueData.settings.scoring.tePremiumPPR > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">TE Premium:</span>
                        <span className="font-medium text-purple-300">+{leagueData.settings.scoring.tePremiumPPR} PPR</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Starting Lineup</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">QB:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.roster.QB}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">RB:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.roster.RB}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">WR:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.roster.WR}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">TE:</span>
                      <span className="font-medium text-gray-100">{leagueData.settings.roster.TE}</span>
                    </div>
                    {leagueData.settings.roster.FLEX > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">FLEX:</span>
                        <span className="font-medium text-gray-100">{leagueData.settings.roster.FLEX}</span>
                      </div>
                    )}
                    {leagueData.settings.roster.SUPERFLEX && leagueData.settings.roster.SUPERFLEX > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">SUPERFLEX:</span>
                        <span className="font-medium text-purple-300">{leagueData.settings.roster.SUPERFLEX}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Impact Summary */}
              <div className="bg-gradient-to-r from-fiesta-blue/10 to-purple-600/10 border border-fiesta-blue/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-fiesta-blue" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span className="font-medium text-fiesta-blue">Settings Applied</span>
                </div>
                <p className="text-sm text-gray-300">
                  Player values and rankings have been recalculated using your league's specific scoring system. 
                  {leagueData.settings.scoring.pointsPerReception > 0 && ' PPR scoring boosts receiving players.'}
                  {leagueData.settings.superflex && ' Superflex format increases QB values.'}
                  {leagueData.settings.scoring.tePremiumPPR > 0 && ' TE premium scoring applied.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Player Search */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for players to add..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-fiesta-blue focus:border-transparent transition-all"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <LoadingSpinner size="sm" />
              </div>
            )}
            
            {searchQuery && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-600 rounded-xl shadow-xl max-h-80 overflow-y-auto z-10">
                {searchResults.length > 0 ? (
                  searchResults.map((player) => (
                    <div
                      key={player.id}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-fiesta-blue/10 transition border-b border-gray-700 last:border-b-0"
                      onClick={() => handleAddPlayer(player)}
                    >
                      <img
                        src={player.imageUrl}
                        alt={player.name}
                        className="h-10 w-10 rounded-full object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-100">{player.name}</div>
                        <div className="text-sm text-gray-400">
                          {player.position} • {player.team} • Value: {(player.value || 0).toFixed(2)}
                        </div>
                      </div>
                      <button className="px-3 py-2 bg-fiesta-blue/20 hover:bg-fiesta-blue/30 text-fiesta-blue text-sm rounded transition-colors touch-manipulation">
                        Add
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="py-4 text-center text-gray-400">No players found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Roster Display */}
        {roster.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 text-lg mb-4">Your roster is empty</div>
            <p className="text-gray-500">Start by searching and adding players above or import from Sleeper</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
            {getRosterByPosition().map(({ position, players }) => (
              <div key={position} className="bg-gray-800 border border-gray-600 rounded-xl p-4 md:p-6">
                <h3 className="text-lg font-bold text-fiesta-blue mb-4 text-center">
                  {position} ({players.length})
                </h3>
                <div className="space-y-3">
                  {players.length === 0 ? (
                    <div className="text-center text-gray-500 text-sm py-4">
                      No {position} players
                    </div>
                  ) : (
                    players.map((player) => (
                      <div key={player.id} className="bg-gray-700/50 rounded-lg p-3 group hover:bg-gray-700/70 transition-colors">
                        <div className="flex items-center gap-3">
                          <img
                            src={player.imageUrl}
                            alt={player.name}
                            className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://a.espncdn.com/i/headshots/nophoto.png'; }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-100 text-sm truncate">{player.name}</div>
                            <div className="text-xs text-gray-400">{player.team}</div>
                            <div className="text-xs text-fiesta-yellow">Value: {(player.value || 0).toFixed(2)}</div>
                          </div>
                          <button
                            onClick={() => removeFromRoster(String(player.id))}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all flex-shrink-0 p-1"
                            title="Remove player"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {rosterLoading && (
          <div className="fixed bottom-4 right-4 bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 flex items-center gap-2">
            <LoadingSpinner size="sm" />
            <span className="text-gray-300 text-sm">Saving roster...</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default MyRoster; 